import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { memo, useCallback } from "react";
import {
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
} from "@mui/material";
import { useField } from "react-final-form";
import { FilledTextArea } from "../../../../components/FilledTextArea";
import {
  formatUrlDisplayValue,
  isLikelyAbsoluteUrl,
  validateUrlDisplayValue,
} from "../../utils/configFieldDisplay";

type Props = {
  name: string;
  label: string;
  value?: unknown;
  disabled?: boolean;
};

export const UrlField = memo((props: Props) => {
  const {
    name,
    label,
    value,
    disabled,
  } = props;
  const { input } = useField<string | undefined>(name, {
    subscription: { value: true },
  });
  const displayValue = formatUrlDisplayValue(disabled ? value : input.value);
  const canOpen = isLikelyAbsoluteUrl(displayValue);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (canOpen) {
        window.open(displayValue, "_blank", "noopener,noreferrer");
      }
    },
    [canOpen, displayValue],
  );

  const handleCopy = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      void navigator.clipboard?.writeText(displayValue);
    },
    [displayValue],
  );

  return (
    <FilledTextArea
      {...(disabled ? { value: displayValue } : {})}
      name={name}
      label={label}
      type="url"
      autoComplete="url"
      fieldProps={{
        parse: formatUrlDisplayValue,
        validate: validateUrlDisplayValue,
      }}
      inputProps={{
        disabled,
        inputMode: "url",
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Stack direction="row" spacing={0.25}>
              <Tooltip title="Open link">
                <span>
                  <IconButton
                    aria-label={`Open ${label}`}
                    edge="end"
                    size="small"
                    onClick={handleOpen}
                    disabled={!canOpen}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Copy link">
                <span>
                  <IconButton
                    aria-label={`Copy ${label}`}
                    edge="end"
                    size="small"
                    onClick={handleCopy}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </InputAdornment>
        ),
      }}
    />
  );
});
